/**
 * Whole minutes between two moments, truncated toward zero.
 *
 * GRAVY-10823. This is `ApproveTimesheet`'s own arithmetic —
 * `((checkout - checkin) / 60).to_i` via `TimesheetService.difference_in_minutes`
 * — and it is what lands in `timesheets.approved_minutes` on Approve. Anything
 * the modal shows has to start here, or it previews a number the approval will
 * not produce. `Math.trunc` and Ruby's `.to_i` both truncate toward zero, so
 * they agree on a negative span too.
 */
export const workedMinutes = (
  checkinAt?: string | null,
  checkoutAt?: string | null,
): number | null => {
  if (!checkinAt || !checkoutAt) return null;

  const start = Date.parse(checkinAt);
  const end = Date.parse(checkoutAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;

  return Math.trunc((end - start) / 60_000);
};

/**
 * The job page's `getRoundOff`, reproduced on purpose — float behaviour and
 * all.
 *
 * GRAVY-10823, and a DELIBERATE decision worth stating plainly because it
 * looks like a bug and reads like one:
 *
 * `Math.floor(value * 100)` is not a faithful truncation. `4.35 * 100` is
 * `434.99999999999994` in IEEE-754, so 261 minutes floors to 4.34 rather than
 * 4.35. The invoice does NOT do this — `TimesheetItem#hours_to_two_decimal`
 * divides in BigDecimal, which is exact, and bills 4.35.
 *
 * So this function knowingly produces a figure up to 0.01 BELOW what the
 * client is charged. It exists because the requirement here is that the three
 * admin surfaces agree with each other, and the job page — `getRoundOff` in
 * `routes/Agency/Job/util.ts`, unchanged since GRAVY-4253 — is the incumbent
 * the other two were asked to match.
 *
 * Copied rather than imported: `Job/util.ts` pulls in `useAuth` and
 * growthbook, and this module is a pure function used in a render path.
 *
 * If `getRoundOff` is ever corrected, correct this with it — they are one
 * decision in two files, and the divergence from the invoice should be closed
 * from the job-page end, not widened from this one.
 */
const roundOffLikeJobPage = (value: number): number =>
  Math.floor(value * 100) / 100;

/**
 * Net Hours = worked time minus the unpaid break (GRAVY-10366).
 *
 * A pure function, deliberately: the AC requires Net Hours to update as the
 * admin edits check-in, check-out or the break — before Approve is clicked —
 * so this runs in local state on every keystroke.
 *
 * ## Why this truncates twice (GRAVY-10823)
 *
 * The first version computed `(end - start) / 3_600_000` and rounded to two
 * decimals, which made this the only surface reporting 4.36 for a timesheet
 * the job page called 4.34 and the modal called 4.37 — three screens, three
 * answers, for one shift.
 *
 * The job page is the incumbent and the agreed target, so this now follows its
 * two steps exactly:
 *
 *   1. the span to whole minutes — what `ApproveTimesheet` stores in
 *      `approved_minutes` via `TimesheetService.difference_in_minutes`, and
 *      therefore what the job page's `netApprovedTime` is made of;
 *   2. `getRoundOff` on the hours, float truncation included.
 *
 * Known and accepted: step 2 lands up to 0.01 below the INVOICED figure, which
 * `TimesheetItem` computes exactly in BigDecimal (261 min bills 4.35, this
 * shows 4.34). See `roundOffLikeJobPage`. Closing that gap means fixing
 * `getRoundOff`, which also moves InvoiceGenerator and PaymentCard, and is
 * deliberately out of scope here.
 *
 * Mirrors `Dashboard::NeedsApprovalWorkers#total_hours`, which was changed to
 * match in the same commit. Returns null when either end is missing or
 * unparseable, which renders as an em dash rather than "NaN".
 */
export const netHours = (
  checkinAt?: string | null,
  checkoutAt?: string | null,
  breakMinutes = 0,
): number | null => {
  const minutes = workedMinutes(checkinAt, checkoutAt);
  if (minutes == null) return null;

  const net = minutes - (breakMinutes || 0);
  // Never negative: a checkout before checkin is the server's error to reject,
  // not something to render as "-2.0 hrs".
  return net < 0 ? 0 : roundOffLikeJobPage(net / 60);
};

/** Break options the modal offers, in minutes. */
export const BREAK_OPTIONS = [0, 15, 30, 45, 60, 90] as const;

/** Server rule: a shift may not span more than a day (`ApproveTimesheet`). */
const MAX_SHIFT_HOURS = 24;

/**
 * The edits the server WILL refuse, detected before Approve is clicked
 * (GRAVY-10812).
 *
 * Approve was relying entirely on `ApproveTimesheet`'s validation, which is
 * correct and stays authoritative — but the modal turned every one of its four
 * specific failures into "Could not approve this timesheet. Please try again.",
 * which is worse than unhelpful: retrying identical data fails identically, so
 * "try again" points away from the data the admin actually has to fix.
 *
 * Mirrors the server's conditions rather than replacing them. Anything this
 * misses is still caught server-side and now surfaced verbatim, so the two can
 * only differ in how EARLY the admin hears about it, never in the outcome.
 *
 * Returns the message to show, or null when the values are submittable.
 */
export const timesheetEditError = (
  checkinAt?: string | null,
  checkoutAt?: string | null,
  breakMinutes = 0,
): string | null => {
  if (!checkinAt || !checkoutAt)
    return 'Enter both a check-in and a check-out time.';

  const start = Date.parse(checkinAt);
  const end = Date.parse(checkoutAt);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return 'Check-in and check-out must be valid dates and times.';
  }

  if (end < start) return 'Check-out is before check-in.';
  if (end === start)
    return 'Check-out is the same as check-in, so no time was worked.';

  const workedHours = (end - start) / 3_600_000;
  if (workedHours > MAX_SHIFT_HOURS) {
    return 'A shift cannot be longer than 24 hours.';
  }
  // GRAVY-10823: compared in WHOLE minutes, because that is the comparison
  // `ApproveTimesheet` actually makes (`approved_break_minutes >= minutes`,
  // against the truncated span). Comparing fractional hours instead could
  // block a break the server would have accepted, on the last partial minute.
  // The server's wording, so the early warning and the rejection read the same.
  const minutes = Math.trunc((end - start) / 60_000);
  if ((breakMinutes || 0) >= minutes) {
    return 'Break time cannot be longer than the time worked.';
  }

  return null;
};
